import type { Handlers } from '@/types/Metrics';

interface KwaiWindow extends Window {
  kwaiq?: {
    instance: (pixelId: string) => {
      track: (eventCode: string, parameters?: Record<string, any>) => void;
    };
  };
}

declare const window: KwaiWindow;

const MAX_RETRY_ATTEMPTS = 10;
const RETRY_DELAY_MS = 300;

export default function (args: Handlers['kwai']) {
  const { tracker } = useRuntimeConfig().public;

  if (!tracker.kwaiPixel) {
    return;
  }

  const [eventCode, parameters] = args;

  try {
    trackKwaiEvent(tracker.kwaiPixel, eventCode, parameters);
  } catch (err) {
    console.error('Error executing Kwai tracking:', err);
  }
}

function trackKwaiEvent(
  pixelId: string,
  eventCode: string,
  parameters?: Record<string, any>,
  attempt: number = 0,
): void {
  if (attempt >= MAX_RETRY_ATTEMPTS) {
    console.warn('Kwai tracking: Maximum retry attempts exceeded');

    return;
  }

  if (isKwaiAvailable()) {
    executeKwaiTracking(pixelId, eventCode, parameters);

    return;
  }

  setTimeout(() => {
    trackKwaiEvent(pixelId, eventCode, parameters, attempt + 1);
  }, RETRY_DELAY_MS);
}

function isKwaiAvailable(): boolean {
  return typeof window !== 'undefined' && window.kwaiq?.instance !== undefined;
}

function executeKwaiTracking(
  pixelId: string,
  eventCode: string,
  parameters?: Record<string, any>,
): void {
  try {
    window.kwaiq!.instance(pixelId).track(eventCode, parameters);
  } catch (error) {
    console.error('Error executing Kwai tracking:', error);
  }
}
